/* eslint-disable complexity */
import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import ReactCountryFlag from 'react-country-flag';
import Localize from 'react-intl-universal';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';

import { compareAsc } from 'date-fns';
import { get } from 'lodash-es';

import CopyrightIcon from '@mui/icons-material/Copyright';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import * as globalConstants from '@common/Constants.js';
import { countryISOCodeByCountry } from '@common/Countries';
import { BOOKING_PROCESS } from '@common/network';
import { checkIsEventFree, isNavigatedWithPrivateLink } from '@common/Util';
import AudioPreview from '@components/AudioPreview/AudioPreview';
import BadUrlMessage from '@components/BadUrlMessage/BadUrlMessage';
import EventNotAvailable from '@components/EventNotAvailable/EventNotAvailable';
import RichTextEditor, { readStyledText } from '@components/RichTextEditor/RichTextEditor';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import * as routerConstants from '@navigation/Constants.js';
import useDetailsTabs from '@pages/EventDetails/hooks/useDetailsTabs';
import { getByPathAndParams } from '@services/BaseApi';

import * as constants from './Constants';
import CreateCancellationForm, { allowedCancellationReasons } from './CreateCancellationForm';
import EventDetailsTabs from './EventDetailsTabs';
import ShowInterestDialog from './ShowInterestDialog/ShowInterestDialog';
import { useShowInterestOptions } from './Util';

const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'bookingNumberUuid'
})(({ bookingNumberUuid }) => ({
  minHeight: bookingNumberUuid ? 'calc(100vh - 8.4rem)' : 'calc(100vh - 5.4rem)'
}));

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isMargin'
})(({ isMargin }) => ({
  marginTop: isMargin ? '1rem' : 0
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette?.action?.main,
  color: theme.palette.common.white,
  fontWeight: '700',
  textTransform: 'none',
  width: '8rem',
  height: 'fit-content',
  fontSize: '14px'
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.light,
  textDecoration: 'none'
}));

const StyledTypography = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 'bold'
}));

const StyledStatus = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: theme.palette.text.disabled,
  paddingLeft: '5px'
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: '2rem',
  gap: '1rem',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '2rem'
  }
}));

const StyledInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  }
}));

const StyledBoxCopy = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.grey[100],
  textAlign: 'left',
  lineHeight: 1
}));

const invitedParticipantStatuses = [
  constants.ParticipantStatus.ToBeInvited,
  constants.ParticipantStatus.Invited
];

const EventDetails = ({ configuration }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [contextData] = useGlobalContext();

  const disableNavigation = isNavigatedWithPrivateLink(location);
  const searchParams = new URLSearchParams(location.search);

  const leftColRef = useRef(null);
  const [leftHeight, setLeftHeight] = useState(null);

  const [activeItem, setActiveItem] = useState(0);
  const [cancelBooking, setCancelBooking] = useState(false);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [cancellationSuccess, setCancellationSuccess] = useState(false);
  const [openShowInterestDialog, setOpenShowInterestDialog] = useState(false);

  const participantId = searchParams?.get('participant') || null;
  const isPrivate = searchParams?.get('isPrivate') || '';
  const bookingNumberUuid = searchParams?.get('bnu') || '';
  const bookingNumber = searchParams?.get('bn') || '';
  const bookingStatusId = searchParams?.get('bs') || '';

  const { fetchShowInterestOptions } = useShowInterestOptions();

  const {
    fetchBilling,
    fetchParticipants,
    bookingData,
    bookingParticipantsData,
    fetchConsents,
    consents,
    isLoading,
    event,
    isInvalidUrl,
    isHiddenEvent,
    isBookingDisabled,
    isEventAlreadyStarted
  } = useDetailsTabs(id, bookingNumberUuid, participantId);

  const descriptionString = readStyledText(event, 'description');
  const isEmptyDescription = !descriptionString;

  const isFreeEvent = checkIsEventFree(event);

  const isParticipantInvitedOrToBeInvited = (event) => {
    return invitedParticipantStatuses.includes(event?.eventParticipant?.status?.id);
  };

  const isNonCancellationReason = (cancellationReasons) => {
    return !cancellationReasons.length;
  };

  useEffect(() => {
    fetchShowInterestOptions();
  }, []);

  useEffect(() => {
    if (isParticipantInvitedOrToBeInvited(event) && isNonCancellationReason(cancellationReasons)) {
      const fetchCancellationReasons = async () => {
        const { data } = await getByPathAndParams({
          path: BOOKING_PROCESS.GET_CANCELLATION_REASONS,
          temApi: true
        });
        setCancellationReasons(
          data?.participantcancellationreason?.filter((reason) =>
            allowedCancellationReasons.includes(reason?.id)
          ) || []
        );
      };
      fetchCancellationReasons();
    }
  }, [event?.eventParticipant?.status?.id]);

  const memoizedImageSource = useMemo(() => {
    if (event?.catalogImage) {
      return `data:image/jpeg;base64,${event.catalogImage}`;
    }
    const defaultImg =
      configuration?.EventsDetailsContent?.defaultImage || 'catalog-placeholder.jpg';

    try {
      return new URL(`../../assets/Images/${defaultImg}`, import.meta.url).href;
    } catch (e) {
      console.warn('Default image not found:', defaultImg);
      return null;
    }
  }, [event?.catalogImage, configuration?.EventsDetailsContent?.defaultImage]);

  useLayoutEffect(() => {
    if (!leftColRef.current) return;

    const el = leftColRef.current;

    const ro = new ResizeObserver(() => {
      const h = el.getBoundingClientRect().height;
      setLeftHeight(Math.max(0, Math.floor(h)));
    });

    ro.observe(el);

    return () => ro.disconnect();
  }, [memoizedImageSource]);

  const handleItemClick = (e, newValue) => {
    setActiveItem(newValue);
  };

  const handleRegisterButtonClick = () => {
    navigate(routerConstants.routes.name.eventBooking, {
      state: {
        from: Localize.get('EventDetails.eventDetails'),
        path: routerConstants.routes.name.eventDetails,
        data: event,
        goBack: location?.pathname + location?.search,
        disableNavigation: disableNavigation
      }
    });
  };
  const previousPath = location?.state?.path || '';
  const previousPathName = location?.state?.from || '';

  const today = new Date();
  const isBookingDeadlineValid = compareAsc(today, event?.registrationDeadline || today) === -1;

  const showLink =
    !!previousPath && !!previousPathName && !disableNavigation && contextData?.isCatalogVisible;

  const renderBasedOnParticipantStatus = (event) => {
    if (cancellationSuccess) {
      return (
        <Paper variant='outlined' sx={{ padding: 2 }}>
          <Typography align='center'>{Localize.get('Booking.participationCancelled')}</Typography>
        </Paper>
      );
    }

    if (
      event?.status?.id === globalConstants.EVENT_STATUS_MAP.IN_PREVIEW &&
      event?.showInterestProcessEnabled
    ) {
      return (
        <StyledButton
          onClick={() => setOpenShowInterestDialog(true)}
          size='small'
          sx={{ width: { xs: '100%', sm: 'fit-content' } }}
          variant='contained'
          endIcon={
            <Tooltip
              title={Localize.get('EventDetails.showInterestTooltip')}
              arrow
              placement='bottom'
            >
              <HelpOutlineIcon />
            </Tooltip>
          }
        >
          {Localize.get('EventDetails.showInterest')}
        </StyledButton>
      );
    }

    if (!isPrivate || !participantId) {
      return (
        <>
          <Tooltip
            title={Localize.get('EventDetails.deadlinePassed')}
            arrow
            placement='right'
            disableHoverListener={
              !event?.registrationDeadline || isBookingDeadlineValid || isEventAlreadyStarted
            }
            disableFocusListener
          >
            <span>
              <StyledButton
                sx={{ width: { xs: '100%', sm: '14rem' } }}
                variant='contained'
                onClick={handleRegisterButtonClick}
                disabled={
                  event?.status?.id === globalConstants.EVENT_STATUS_MAP.COMPLETED ||
                  event?.status?.id === globalConstants.EVENT_STATUS_MAP.CANCELLED ||
                  (event?.registrationDeadline && !isBookingDeadlineValid) ||
                  isBookingDisabled ||
                  isEventAlreadyStarted
                }
                aria-describedby={
                  !isEventAlreadyStarted &&
                  ((event?.registrationDeadline && !isBookingDeadlineValid) ||
                    event?.status?.id === globalConstants.EVENT_STATUS_MAP.COMPLETED)
                    ? 'deadline-tooltip'
                    : undefined
                }
              >
                {event?.status?.id === globalConstants.EVENT_STATUS_MAP.CANCELLED
                  ? Localize.get('RegistrationForm.eventCancelled')
                  : event?.status?.id === globalConstants.EVENT_STATUS_MAP.COMPLETED
                    ? Localize.get('RegistrationForm.eventCompleted')
                    : isEventAlreadyStarted
                      ? Localize.get('EventDetails.eventStartPassed')
                      : isFreeEvent
                        ? Localize.get('RegistrationForm.registerForFree')
                        : Localize.get('RegistrationForm.book')}
              </StyledButton>
            </span>
          </Tooltip>
          {/* Hidden element that screen readers can announce */}
          {event?.registrationDeadline && !isBookingDeadlineValid && !isEventAlreadyStarted && (
            <span id='deadline-tooltip' style={{ display: 'none' }}>
              {Localize.get('EventDetails.deadlinePassed')}
            </span>
          )}
        </>
      );
    }

    return invitedParticipantStatuses.includes(event?.eventParticipant?.status?.id) ? (
      <>
        <Stack direction='row' spacing={1} sx={{ width: 'auto', margin: '5px 0' }}>
          <StyledButton
            variant='contained'
            onClick={handleRegisterButtonClick}
            disabled={
              (event?.registrationDeadline && !isBookingDeadlineValid) ||
              isBookingDisabled ||
              isEventAlreadyStarted
            }
          >
            {Localize.get('EventDetails.accept')}
          </StyledButton>
          <StyledButton
            variant='contained'
            onClick={() => setCancelBooking(true)}
            disabled={isBookingDisabled}
          >
            {Localize.get('EventDetails.decline')}
          </StyledButton>
        </Stack>

        {!!cancellationReasons?.length && (
          <CreateCancellationForm
            cancelBooking={cancelBooking}
            setCancelBooking={setCancelBooking}
            cancellationReasons={cancellationReasons}
            uuid={event?.eventParticipant?.attendeeDto?.uuid}
            setCancellationSuccess={setCancellationSuccess}
          />
        )}
      </>
    ) : (
      <Paper
        variant='outlined'
        sx={(theme) => ({
          padding: theme.spacing(2)
        })}
      >
        <StyledTypography align='center'>{event?.eventParticipantMessage || ''}</StyledTypography>
      </Paper>
    );
  };

  if (isInvalidUrl) {
    return <BadUrlMessage />;
  }

  if (isHiddenEvent) {
    return <EventNotAvailable />;
  }

  return (
    <StyledContainer component='main' bookingNumberUuid={bookingNumberUuid}>
      {isLoading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='80vh'>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {showLink && (
            <Box mb={1.9} pt={1.5} sx={{ textAlign: 'end' }}>
              <Typography variant='subtitle1'>
                <StyledLink to={{ pathname: previousPath }}>{previousPathName}</StyledLink>
                <span>{' / '}</span>
                <span>{Localize.get('Catalog.eventsTabTitle')}</span>
                <span>{' / '}</span>
                <span>{Localize.get('EventDetails.detailsInfo')}</span>
              </Typography>
            </Box>
          )}

          <StyledCard isMargin={bookingNumberUuid || !contextData?.isCatalogVisible}>
            <CardContent pt={1} sx={{ paddingBottom: 0 }}>
              <StyledBox>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                    {event?.name}
                  </Typography>
                  <StyledInfo>
                    <Typography sx={{ textAlign: 'left' }}>
                      {event?.type?.value || ''}
                      {get(event, 'nativeLanguages') &&
                        get(event, 'nativeLanguages').map((lang) => (
                          <ReactCountryFlag
                            key={lang.key}
                            countryCode={countryISOCodeByCountry[lang.key.toLowerCase()]}
                            svg
                            alt={lang.value}
                            style={{
                              width: '1.2rem',
                              height: 'auto',
                              marginLeft: '1.3rem'
                            }}
                          />
                        ))}
                    </Typography>
                    {/* Booking Status */}
                    {bookingStatusId && (
                      <StyledInfo>
                        <Typography
                          component='span'
                          sx={{
                            fontWeight: 'bold',
                            marginLeft: { xs: 0, sm: '1rem' },
                            marginRight: 0
                          }}
                        >
                          {Localize.get('EventDetails.bookingStatus')}:
                        </Typography>
                        <StyledStatus component='span'>
                          {globalConstants.bookingStatusIds[bookingStatusId] || ''}
                        </StyledStatus>
                      </StyledInfo>
                    )}
                    {/* Booking Number */}
                    {bookingNumber && (
                      <StyledInfo>
                        <Typography
                          component='span'
                          sx={{
                            fontWeight: 'bold',
                            marginLeft: { xs: 0, sm: '1rem' },
                            marginRight: 0
                          }}
                        >
                          {Localize.get('EventDetails.bookingNumber')}:
                        </Typography>
                        <StyledStatus component='span'>{bookingNumber}</StyledStatus>
                      </StyledInfo>
                    )}
                    {!!event?.audioDescription?.url && (
                      <AudioPreview
                        url={event?.audioDescription.url}
                        name={event?.audioDescription?.name}
                      />
                    )}
                  </StyledInfo>
                </Box>
                {!bookingNumberUuid && renderBasedOnParticipantStatus(event)}
              </StyledBox>

              <Grid container spacing={2} alignItems='flex-start'>
                <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex' }}>
                  <Box ref={leftColRef} sx={{ width: '100%' }}>
                    <CardMedia
                      component='img'
                      src={memoizedImageSource}
                      alt='Event Image'
                      sx={{
                        width: '100%',
                        objectFit: 'cover'
                      }}
                    />

                    {event?.copyrightInfo && (
                      <StyledBoxCopy>
                        <CopyrightIcon sx={{ fontSize: '0.7rem', mr: 0.5 }} />
                        <Typography sx={{ fontSize: '0.7rem', mt: '0 !important' }}>
                          {event?.copyrightInfo}
                        </Typography>
                      </StyledBoxCopy>
                    )}
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={8}
                  md={9}
                  sx={{
                    textAlign: 'left',
                    display: 'flex',
                    height: '100%',
                    minHeight: 0
                  }}
                >
                  <Box
                    sx={{
                      maxHeight: leftHeight ? `${leftHeight}px` : 'none',
                      overflow: leftHeight ? 'auto' : 'visible'
                    }}
                  >
                    {!isEmptyDescription ? (
                      <RichTextEditor initialValue={event?.description} disabled isToolbarHidden />
                    ) : (
                      <Typography>{Localize.get('CourseDetails.noDesc')}</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          <EventDetailsTabs
            event={event}
            configuration={configuration}
            activeItem={activeItem}
            handleItemClick={handleItemClick}
            fetchBilling={fetchBilling}
            fetchParticipants={fetchParticipants}
            bookingParticipantsData={bookingParticipantsData}
            bookingData={bookingData}
            fetchConsents={fetchConsents}
            bookingNumberUuid={bookingNumberUuid}
            consents={consents}
            isBooking={!!bookingNumberUuid}
          />

          <ShowInterestDialog
            event={event}
            openShowInterestDialog={openShowInterestDialog}
            setOpenShowInterestDialog={setOpenShowInterestDialog}
          />
        </>
      )}
    </StyledContainer>
  );
};

export default EventDetails;
